import {
  CheckCircleIcon,
  CheckIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  InformationCircleIcon,
  SparklesIcon,
  XMarkIcon,
  ArrowLeftIcon,
  UserPlusIcon,
  UsersIcon,
  TrashIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import {
  ConfirmationModal,
  ModalVariant,
} from '../../components/ConfirmationModal/ConfirmationModal';
import { DocumentTocSidebar } from '../../components/DocumentTocSidebar';
import { ErrorMessage } from '../../components/ErrorMessage/ErrorMessage';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import { MonacoMarkdownEditor } from '../../components/monaco/MonacoMarkdownEditor';
import { SectionCommentThread } from '../../components/SectionCommentThread';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { contextManager } from '../../services/contextManager';
import { generationService } from '../../services/generationService';
import { notifyPeerReviewChanged } from '../../services/peerReviewEvents';
import { projectService } from '../../services/projectService';
import { reviewService } from '../../services/reviewService';
import { ProjectItem, ProjectSection } from '../../types/project';
import {
  Review,
  ReviewerRole,
  ReviewerStatus,
  Comment,
  CommentType,
  COMMENT_TYPE_REQUEST_CHANGES,
  PeerReviewItem,
} from '../../types/review';
import { buildDocumentToc } from '../../utils/documentToc';
import { logger } from '../../utils/logger';
import {
  isCurrentUserProjectSteward,
  isProjectStewardReviewer,
  isProjectStewardUser,
  PROJECT_STEWARD_PARTICIPANT_MESSAGE,
  reviewerMatchesCurrentUser,
} from '../../utils/reviewParticipants';
import {
  formatParticipantSummary,
  getReviewParticipantCounts,
} from '../../utils/reviewSummary';

import { PeerReviewTable } from './PeerReviewTable';

const REVIEW_ACCESS_DENIED_MESSAGE =
  'Access denied: You are not assigned as a participant for this architecture review.';
const REVIEWER_ADD_FAILED_MESSAGE =
  'Failed to add reviewer. Please try again.';
const PARTICIPANT_MANAGEMENT_FORBIDDEN_MESSAGE =
  'Only the project owner or co-owner can edit review participants.';
const EDIT_PARTICIPANTS_BUTTON_TEXT = 'Edit Participants';
const EDIT_PARTICIPANTS_APPROVED_TITLE =
  'Participants cannot be edited after the review is approved.';
const OVERVIEW_SECTION_ID = 'overview';
const APPROVER_ROLE = 'approver';
const APPROVED_REVIEWER_STATUS = 'approved';
const APPROVED_REVIEW_STATUS = 'approved';
const REQUEST_CHANGES_SECTION_TITLE = '⚠ Requested Changes';
const REVIEW_DECISIONS_SECTION_TITLE = '✅ Review Decisions';
const APPROVAL_DECISION_PREFIX = '**Review Decision: APPROVED**';
const UNRESOLVED_COMMENTS_WARNING =
  'You have unresolved comments. Do you want to approve the project?';
const REQUEST_CHANGES_COMMENT_REQUIRED_MESSAGE =
  'A request changes decision requires a comment.';
const APPROVAL_BLOCKED_TOOLTIP =
  'Resolve your requested changes before approving.';
const PEER_REVIEW_INCOMPLETE_TOOLTIP =
  'Confirm every peer review checklist item (all green) before approving.';
const REVIEWER_BANNER_TEXT =
  'You have reviewer access, which means you can view and write comments. Only designated approver can approve or request changes and check the peer review items';
const PARTICIPANT_ROLE_REQUIRED_MESSAGE =
  'Select whether the participant should be a reviewer or approver before adding them.';
const FULL_DOCUMENT_SECTION_SCROLL_DELAY_MS = 50;
const DEFAULT_DOCUMENT_FILENAME = 'document';
const DEFAULT_EXPORT_EXTENSION = '.md';
const EXPORT_FAILED_MESSAGE = 'Export failed';
const EXPORT_OPTIONS = [
  { format: 'markdown', label: 'Markdown' },
  { format: 'pdf', label: 'PDF' },
  { format: 'docx', label: 'Word' },
] as const;
const EXPORT_EXTENSION_BY_FORMAT: Record<string, string> = {
  markdown: '.md',
  pdf: '.pdf',
  docx: '.docx',
};
const FIX_HIGHLIGHT_CLASSNAME = 'archie-fix-highlight';
const FIX_HIGHLIGHT_REMOVED_CLASSNAME = 'archie-fix-removed';
const FIX_HIGHLIGHT_BACKGROUND = '#fff7b4';
const FIX_HIGHLIGHT_BORDER = '#ffc600';
const FIX_ADDED_BACKGROUND = '#e7f6ee';
const FIX_ADDED_BORDER = '#008557';
const FIX_REMOVED_BACKGROUND = '#fff2f0';
const FIX_REMOVED_BORDER = '#dd1d21';
const KEEP_LABEL = 'Keep';
const UNDO_LABEL = 'Undo';
// Only an active (succeeded) fix has live highlights/changes in the document;
// an "undone" fix has had its additions removed, so it is excluded here.
const FIX_STATUS_SUCCEEDED = 'succeeded';

interface SectionFixSummary {
  checklistItemId: number;
  summary: string;
  completedAt: string | null;
  kept: boolean;
  canKeep?: boolean;
  canUndo?: boolean;
  changes: Array<{
    description: string;
    highlightedText: string;
    className: string;
  }>;
}

const ARCHIE_FIX_COMMENT_AUTHOR = 'Archie Agent';

/**
 * Renders an applied "Let Archie Fix This" change as a comment-style card for
 * the Peer Review Comments tab: Archie as the author, the highlighted
 * added/removed text and Keep/Undo controls. It reuses the same data
 * (sectionFixSummaries) and decision handler (handleFullDocumentAction) as the
 * Full Document view, so a Keep/Undo here mirrors there and in the checklist
 * tab. Keep/Undo are owner/co-owner only; reviewers see a read-only "Kept"
 * badge.
 */
const ArchieSectionFixCard: React.FC<{
  summary: SectionFixSummary;
  sectionId: string;
  canDecide: boolean;
  isApplying: boolean;
  onAction: (sectionId: string, action: 'keep' | 'undo') => void;
}> = ({ summary, sectionId, canDecide, isApplying, onAction }) => {
  const timestamp = summary.completedAt
    ? new Date(summary.completedAt).toLocaleString()
    : '';
  return (
    <div className="mb-3 rounded-lg border border-[#008557]/30 bg-[#008557]/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#008557] text-white">
            <SparklesIcon className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {ARCHIE_FIX_COMMENT_AUTHOR}
            </p>
            {timestamp && (
              <p className="text-[11px] text-gray-500">
                Applied a fix · {timestamp}
              </p>
            )}
          </div>
        </div>
        {canDecide ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={isApplying || summary.kept}
              onClick={() => onAction(sectionId, 'keep')}
              aria-label={KEEP_LABEL}
              title={KEEP_LABEL}
              className={
                summary.kept
                  ? 'inline-flex items-center justify-center rounded-md border border-[#008557] bg-[#008557] p-1.5 text-white'
                  : 'inline-flex items-center justify-center rounded-md border border-[#008557]/30 bg-white p-1.5 text-[#00614a] transition-colors hover:bg-[#008557]/5'
              }
            >
              <CheckIcon className="h-4 w-4" />
            </button>
            {!summary.kept && (
              <button
                type="button"
                disabled={isApplying}
                onClick={() => onAction(sectionId, 'undo')}
                aria-label={UNDO_LABEL}
                title={UNDO_LABEL}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-500 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        ) : (
          summary.kept && (
            <span
              aria-label="Kept"
              title="Kept"
              className="inline-flex items-center justify-center rounded-md border border-[#008557] bg-[#008557] p-1.5 text-white"
            >
              <CheckIcon className="h-4 w-4" />
            </span>
          )
        )}
      </div>
      {summary.summary && (
        <p className="mt-2 whitespace-pre-wrap text-sm leading-snug text-gray-700">
          {summary.summary}
        </p>
      )}
      {summary.changes.length > 0 && (
        <ul className="mt-3 space-y-2">
          {summary.changes.map((change, index) => {
            const removed =
              change.className === FIX_HIGHLIGHT_REMOVED_CLASSNAME;
            return (
              <li key={`${sectionId}-fix-${index}`}>
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-0.5 inline-flex flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                      removed
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {removed ? 'Removed' : 'Added'}
                  </span>
                  <span className="text-xs text-gray-700">
                    {change.description}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

type ParticipantRoleSelection = ReviewerRole | '';

const getCommentsBySection = (comments: Comment[]): Record<string, Comment[]> =>
  comments.reduce(
    (accumulator, comment) => {
      if (!accumulator[comment.section_id]) {
        accumulator[comment.section_id] = [];
      }
      accumulator[comment.section_id].push(comment);
      return accumulator;
    },
    {} as Record<string, Comment[]>,
  );

const getRequestChangeThreadIds = (overviewComments: Comment[]): Set<string> => {
  const requestChangeThreadIds = new Set<string>();
  const pendingThreadIds = overviewComments
    .filter(
      (comment) =>
        comment.comment_type === COMMENT_TYPE_REQUEST_CHANGES && !comment.parent_id,
    )
    .map((comment) => comment.id);

  while (pendingThreadIds.length > 0) {
    const commentId = pendingThreadIds.pop();
    if (!commentId || requestChangeThreadIds.has(commentId)) {
      continue;
    }

    requestChangeThreadIds.add(commentId);
    overviewComments
      .filter((comment) => comment.parent_id === commentId)
      .forEach((comment) => pendingThreadIds.push(comment.id));
  }

  return requestChangeThreadIds;
};

type ReviewTabId = 'sections' | 'peer-checklist' | 'full';

const PEER_CHECKLIST_TAB_ID: ReviewTabId = 'peer-checklist';

const TAB_ACTIVE_CLASSNAME = 'border-[#008557] text-[#008557]';
const TAB_INACTIVE_CLASSNAME =
  'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300';

const isSectionFixKept = (item: PeerReviewItem): boolean =>
  (item.architect_comments ?? []).some((comment) =>
    comment.message?.startsWith('Keep Archie'),
  );

interface ReviewHeaderProps {
  onBack: () => void;
  canManageParticipants: boolean;
  isReviewApproved: boolean;
  onEditParticipants: () => void;
  visibleParticipants: Review['reviewers'];
  participantSummary: string;
  approvedApproversLabel: string;
  review: Review;
  conversationCount: number;
  canRequestChanges: boolean;
  canApprove: boolean;
  currentReviewerId: string | null;
  openRequestChangesCount: number;
  allPeerReviewChecked: boolean;
  submitting: boolean;
  onOpenConfirmationModal: (status: ReviewerStatus) => void;
  onOpenRequestedChangesView: () => void;
  isReviewer: boolean;
  isOwner: boolean;
  canSelfReview: boolean;
}

const ReviewHeader: React.FC<ReviewHeaderProps> = ({
  onBack,
  canManageParticipants,
  isReviewApproved,
  onEditParticipants,
  visibleParticipants,
  participantSummary,
  approvedApproversLabel,
  review,
  conversationCount,
  canRequestChanges,
  canApprove,
  currentReviewerId,
  openRequestChangesCount,
  allPeerReviewChecked,
  submitting,
  onOpenConfirmationModal,
  onOpenRequestedChangesView,
  isReviewer,
  isOwner,
  canSelfReview,
}) => (
  <div className="bg-base-100 border-b border-base-300 px-6 py-4 flex-shrink-0">
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <button onClick={onBack} className="btn btn-ghost btn-sm gap-2">
          <ArrowLeftIcon className="w-5 h-5" />
          Back to Reviews
        </button>
        <div className="flex items-stretch gap-3">
          {canManageParticipants && (
            <button
              onClick={onEditParticipants}
              disabled={isReviewApproved}
              aria-label={EDIT_PARTICIPANTS_BUTTON_TEXT}
              className={
                isReviewApproved
                  ? 'inline-flex items-center justify-center rounded-lg border border-gray-200 bg-gray-100 p-2.5 text-gray-400 cursor-not-allowed'
                  : 'inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2.5 text-gray-700 transition-colors hover:bg-gray-50'
              }
              title={
                isReviewApproved
                  ? EDIT_PARTICIPANTS_APPROVED_TITLE
                  : EDIT_PARTICIPANTS_BUTTON_TEXT
              }
            >
              <UserPlusIcon className="w-4 h-4" />
            </button>
          )}
          {/* Reviewers Summary */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50">
            <div className="flex -space-x-2">
              {visibleParticipants.slice(0, 3).map((reviewer) => (
                <div
                  key={reviewer.id}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white border-2 border-white shadow-sm"
                  style={{
                    backgroundColor: getReviewerAvatarColor(reviewer.status),
                  }}
                  title={`${reviewer.user_name} (${reviewer.role} · ${reviewer.status})`}
                >
                  {reviewer.user_name.charAt(0).toUpperCase()}
                </div>
              ))}
              {visibleParticipants.length > 3 && (
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600 border-2 border-white shadow-sm">
                  +{visibleParticipants.length - 3}
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-700">
                {participantSummary}
              </span>
              <span className="text-xs text-gray-500">
                {approvedApproversLabel}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{review.project_name}</h1>
          <p className="text-sm opacity-60 mt-1">
            {conversationCount} conversation{conversationCount === 1 ? '' : 's'}
          </p>
        </div>
        {(canRequestChanges || canApprove) &&
          review.status !== 'approved' &&
          (() => {
            const myStatus = review.reviewers.find(
              (r) => r.id === currentReviewerId,
            )?.status;
            const approveBlocked =
              openRequestChangesCount > 0 || !allPeerReviewChecked;
            let approveBlockedTooltip: string | undefined;
            if (approveBlocked) {
              approveBlockedTooltip =
                openRequestChangesCount > 0
                  ? APPROVAL_BLOCKED_TOOLTIP
                  : PEER_REVIEW_INCOMPLETE_TOOLTIP;
            }
            if (myStatus === 'approved') {
              return (
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-md text-sm">
                  <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
                  <span>You have approved this architecture.</span>
                </div>
              );
            }
            return (
              <div className="flex gap-3">
                {canRequestChanges && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        onOpenConfirmationModal('changes_requested')
                      }
                      disabled={submitting}
                      className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border-2 border-yellow-500 hover:bg-yellow-50 hover:border-yellow-600 rounded-md transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <HandThumbDownIcon className="w-5 h-5" />
                      Request Changes
                    </button>
                    {openRequestChangesCount > 0 && (
                      <button
                        type="button"
                        onClick={onOpenRequestedChangesView}
                        className="px-3 py-2 text-sm font-medium text-yellow-900 bg-yellow-100 border border-yellow-300 rounded-md hover:bg-yellow-200 transition-colors"
                      >
                        {openRequestChangesCount}
                      </button>
                    )}
                  </div>
                )}
                {canApprove && (
                  <button
                    onClick={() => onOpenConfirmationModal('approved')}
                    disabled={submitting || approveBlocked}
                    title={approveBlockedTooltip}
                    className="px-5 py-2.5 text-sm font-medium text-white bg-[#008557] hover:bg-[#006b45] rounded-md transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                  >
                    <HandThumbUpIcon className="w-5 h-5" />
                    Approve
                  </button>
                )}
              </div>
            );
          })()}
        {canApprove && review.status === 'approved' && (
          <div className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-md text-sm">
            <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
            <span>This architecture has been approved.</span>
          </div>
        )}
        {isReviewer && !canApprove && !isOwner && (
          <div className="flex items-center gap-2 px-3 py-2 bg-info/10 text-info rounded-md text-sm">
            <InformationCircleIcon className="w-5 h-5 flex-shrink-0" />
            <span>{REVIEWER_BANNER_TEXT}</span>
          </div>
        )}
        {isOwner && !isReviewer && (
          <div className="flex items-center gap-2 px-3 py-2 bg-base-200 text-base-content/70 rounded-md text-sm">
            <InformationCircleIcon className="w-5 h-5 flex-shrink-0" />
            <span>
              {canSelfReview
                ? 'You are viewing this review as the project admin. ' +
                  'Owners can manage participants but do not review or ' +
                  'approve.'
                : 'You are viewing this review as the project owner or ' +
                  'co-owner. Owners can manage participants but do not ' +
                  'review or approve.'}
            </span>
          </div>
        )}
      </div>
    </div>
  </div>
);

const getReviewTabClassName = (isActive: boolean): string =>
  `px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
    isActive ? TAB_ACTIVE_CLASSNAME : TAB_INACTIVE_CLASSNAME
  }`;

const REVIEWER_AVATAR_COLORS: Record<string, string> = {
  approved: '#008557',
  changes_requested: '#f59e0b',
};

const getReviewerAvatarColor = (status: string): string =>
  REVIEWER_AVATAR_COLORS[status] ?? '#6b7280';

const REVIEWER_STATUS_BADGE_CLASSES: Record<string, string> = {
  approved: 'bg-green-100 text-green-800',
  changes_requested: 'bg-yellow-100 text-yellow-800',
};

const getReviewerStatusBadgeClass = (status: string): string =>
  REVIEWER_STATUS_BADGE_CLASSES[status] ?? 'bg-gray-100 text-gray-800';

interface ManageReviewersModalProps {
  isOpen: boolean;
  canManageParticipants: boolean;
  isReviewApproved: boolean;
  onClose: () => void;
  visibleParticipants: Review['reviewers'];
  removingReviewer: string | null;
  onRemoveReviewer: (reviewerId: string) => void;
  newReviewerEmail: string;
  onNewReviewerEmailChange: (value: string) => void;
  newReviewerRole: ParticipantRoleSelection;
  onNewReviewerRoleChange: (value: ParticipantRoleSelection) => void;
  onAddNewReviewer: () => void;
  addingReviewer: boolean;
}

const ManageReviewersModal: React.FC<ManageReviewersModalProps> = ({
  isOpen,
  canManageParticipants,
  isReviewApproved,
  onClose,
  visibleParticipants,
  removingReviewer,
  onRemoveReviewer,
  newReviewerEmail,
  onNewReviewerEmailChange,
  newReviewerRole,
  onNewReviewerRoleChange,
  onAddNewReviewer,
  addingReviewer,
}) => {
  if (!isOpen || !canManageParticipants || isReviewApproved) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg w-full max-w-lg shadow-xl overflow-hidden flex flex-col max-h-[80vh]">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-white">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <UsersIcon className="w-5 h-5" />
            Manage Participants
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 rounded-md p-1 hover:bg-gray-100"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-0 overflow-y-auto flex-1">
          {/* Existing Reviewers List */}
          <div className="px-6 py-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Current Participants ({visibleParticipants.length})
            </h4>
            <div className="space-y-3">
              {visibleParticipants.map((reviewer) => (
                <div
                  key={reviewer.id}
                  className="flex items-center justify-between group p-2 hover:bg-gray-50 rounded-lg -mx-2"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white shadow-sm"
                      style={{
                        backgroundColor: getReviewerAvatarColor(reviewer.status),
                      }}
                    >
                      {reviewer.user_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{reviewer.user_name}</div>
                      <div className="text-xs text-gray-500">{reviewer.user_email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Role Badge */}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      reviewer.role === 'approver'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {reviewer.role === 'approver' ? 'Approver' : 'Reviewer'}
                    </span>
                    {/* Status Badge */}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getReviewerStatusBadgeClass(reviewer.status)}`}>
                      {reviewer.status.replace('_', ' ')}
                    </span>

                    {/* Remove Button */}
                    <button
                      onClick={() => onRemoveReviewer(reviewer.id)}
                      disabled={removingReviewer === reviewer.id}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 transition-all focus:opacity-100"
                      title="Remove reviewer"
                    >
                      {removingReviewer === reviewer.id ? (
                        <span className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin block"/>
                      ) : (
                        <TrashIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add Reviewer Section */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Add New Participant
            </h4>
            <div className="flex gap-2">
              <input
                type="email"
                value={newReviewerEmail}
                onChange={(e) => onNewReviewerEmailChange(e.target.value)}
                placeholder="participant@shell.com"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none text-sm"
                onKeyPress={(e) => e.key === 'Enter' && onAddNewReviewer()}
              />
              <select
                value={newReviewerRole}
                onChange={(event) =>
                  onNewReviewerRoleChange(
                    event.target.value as ParticipantRoleSelection,
                  )
                }
                aria-label="Select participant role"
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none text-sm bg-white"
              >
                <option value="" disabled>
                  Select role
                </option>
                <option value="reviewer">Reviewer</option>
                <option value="approver">Approver</option>
              </select>
              <button
                onClick={onAddNewReviewer}
                disabled={!newReviewerEmail.trim() || !newReviewerRole || addingReviewer}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-md flex items-center gap-2 main-button whitespace-nowrap"
              >
                {addingReviewer ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {' '}
                    Adding...
                  </>
                ) : (
                  <>
                    <UserPlusIcon className="w-4 h-4" />
                    Add
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * ReviewDetailPage - Section-by-section review interface
 * Shows architecture sections with collapsible comment threads
 */
export const ReviewDetailPage: React.FC = () => {
  const { reviewId } = useParams<{ reviewId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get('projectId');
  const { user } = useAuth();
  const { profile } = useUserProfile();
  // Personas granted self-review (e.g. Admin) may add themselves as a
  // participant on a project they own or co-own for internal testing.
  const canSelfReview = profile?.can_self_review ?? false;

  const [review, setReview] = useState<Review | null>(null);
  const [project, setProject] = useState<ProjectItem | null>(null);
  const [sections, setSections] = useState<ProjectSection[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<ReviewTabId>(
    PEER_CHECKLIST_TAB_ID,
  );

  // Manage Reviewers Modal State
  const [showManageReviewersModal, setShowManageReviewersModal] = useState(false);
  const [newReviewerEmail, setNewReviewerEmail] = useState('');
  const [newReviewerRole, setNewReviewerRole] =
    useState<ParticipantRoleSelection>('');
  const [addingReviewer, setAddingReviewer] = useState(false);
  const [removingReviewer, setRemovingReviewer] = useState<string | null>(null);

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    action: ReviewerStatus | null;
    variant: ModalVariant;
    title: string;
    message: string;
  }>({
    isOpen: false,
    action: null,
    variant: 'approve',
    title: '',
    message: '',
  });

  // Check if current user is a participant and their approval ability
  const [isReviewer, setIsReviewer] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  // True when the current user is the project owner/co-owner (steward),
  // regardless of whether they are also a review participant. Gates editing of
  // the Archie Review Checklist tab.
  const [isProjectSteward, setIsProjectSteward] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [allPeerReviewChecked, setAllPeerReviewChecked] = useState(false);
  const [currentReviewerId, setCurrentReviewerId] =
    useState<string | null>(null);
  const [currentUserEmail] =
    useState('user@example.com'); // TODO: From auth context
  const requestChangesSectionRef = useRef<HTMLDivElement | null>(null);
  const fullDocumentSectionRefs = useRef<Record<string, HTMLDivElement | null>>(
    {},
  );
  // Per-section anchors in the Peer Review Comments tab (where "View change"
  // now lands, to show the change as an Archie comment + highlighted preview).
  const peerCommentSectionRefs = useRef<Record<string, HTMLDivElement | null>>(
    {},
  );
  // Anchor for the "General Comments" thread, so the table-of-contents sidebar
  // in the Peer Review Comments tab can jump to it.
  const generalCommentsRef = useRef<HTMLDivElement | null>(null);
  const [shouldFocusRequestChanges, setShouldFocusRequestChanges] =
    useState(false);
  const [pendingFullDocumentSectionId, setPendingFullDocumentSectionId] =
    useState<string | null>(null);
  const [exportingFormat, setExportingFormat] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [applyingFullDocumentAction, setApplyingFullDocumentAction] =
    useState<string | null>(null);
  // The peer-review checklist items (with Archie fix data). Loaded separately
  // because the review payload does not embed the checklist. Drives the
  // Full Document tab's fix summaries and green highlights.
  const [peerReviewItems, setPeerReviewItems] = useState<PeerReviewItem[]>([]);

  const sectionFixSummaries = React.useMemo(() => {
    const summariesBySection = new Map<string, SectionFixSummary>();

    peerReviewItems.forEach((item) => {
      if (item.archie_fix?.status !== FIX_STATUS_SUCCEEDED) {
        return;
      }
      item.archie_fix?.changes?.forEach((change) => {
        const existingSummary = summariesBySection.get(change.section_name);
        const nextChange = {
          description: change.change_description,
          highlightedText: change.highlighted_text?.trim() ?? '',
          className: FIX_HIGHLIGHT_CLASSNAME,
        };

        if (existingSummary) {
          existingSummary.changes.push(nextChange);
          return;
        }

        summariesBySection.set(change.section_name, {
          checklistItemId: item.checklist_item_id,
          summary: item.archie_fix?.summary ?? '',
          completedAt:
            item.archie_fix?.completed_at ?? item.archie_fix?.created_at ?? null,
          kept: isSectionFixKept(item),
          canKeep: item.archie_fix?.can_keep ?? true,
          canUndo: item.archie_fix?.can_undo ?? true,
          changes: [nextChange],
        });
      });
    });

    return summariesBySection;
  }, [peerReviewItems]);

  // Stable per-section Archie change descriptions. Deriving these inline in the
  // JSX (fixSummary.changes.map(...)) minted a BRAND NEW array on every render,
  // even when the content was identical. That fresh array identity churned
  // MonacoMarkdownEditor -> MermaidRenderer and left the mermaid spinner stuck
  // (most visibly the Solution Summary section, which contains a diagram).
  // Memoizing keeps each array's identity stable so the diagram effect doesn't
  // needlessly re-fire.
  const archieDescriptionsBySection = React.useMemo(() => {
    const descriptionsBySection = new Map<string, string[]>();
    sectionFixSummaries.forEach((summary, sectionName) => {
      descriptionsBySection.set(
        sectionName,
        summary.changes.map((change) => change.description),
      );
    });
    return descriptionsBySection;
  }, [sectionFixSummaries]);

  // Table-of-contents entries for the sidebar: one per document section, with
  // the ADR section expanded into its ADR sub-items. The idPrefix must match
  // the headingIdPrefix passed to each section's MonacoMarkdownEditor below.
  const peerCommentToc = React.useMemo(
    () => buildDocumentToc(sections, (sectionId) => `sec-${sectionId}`),
    [sections],
  );

  // Smooth-scroll a DOM node into view, nudged below the sticky header.
  const scrollNodeIntoView = (node: Element | null | undefined): void => {
    node?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // The table-of-contents sidebar, shared by the Peer Review Comments and Full
  // Document tabs. `sectionRefs` is the per-tab anchor map used to jump to a
  // section (each tab renders its own copy of the sections, so only one set of
  // anchors is ever mounted). Sub-heading jumps resolve the rendered heading's
  // DOM id, falling back to the section container if it isn't in the DOM.
  const renderDocumentTocSidebar = (
    sectionRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>,
  ) => (
    <DocumentTocSidebar
      toc={peerCommentToc}
      onSelectSection={(sectionId) =>
        scrollNodeIntoView(sectionRefs.current[sectionId])
      }
      onSelectHeading={(sectionId, headingId) => {
        const heading = document.getElementById(headingId);
        if (heading) {
          scrollNodeIntoView(heading);
          return;
        }
        scrollNodeIntoView(sectionRefs.current[sectionId]);
      }}
    />
  );

  // Load the peer-review checklist (with Archie fix data) for the Full
  // Document highlights/summaries; the review payload does not embed it.
  useEffect(() => {
    if (!reviewId) {
      return undefined;
    }
    let cancelled = false;
    reviewService
      .getPeerReview(reviewId)
      .then((result) => {
        if (!cancelled) {
          setPeerReviewItems(result.items);
        }
      })
      .catch(() => {
        /* Non-fatal: highlights/summaries simply won't render. */
      });
    return () => {
      cancelled = true;
    };
  }, [reviewId]);

  const handleFullDocumentAction = async (
    sectionId: string,
    action: 'keep' | 'undo',
  ) => {
    if (!reviewId) {
      return;
    }
    const summary = sectionFixSummaries.get(sectionId);
    if (!summary) {
      return;
    }
    setApplyingFullDocumentAction(`${sectionId}:${action}`);
    try {
      if (action === 'undo') {
        // Removes Archie's additions from the document and reverts the verdict.
        await reviewService.undoPeerReviewFix(reviewId, summary.checklistItemId);
      } else {
        // Keep the changes; log the decision as a comment (document unchanged).
        await reviewService.addArchitectComment(
          reviewId,
          summary.checklistItemId,
          'Keep Archie Agent Solution Summary improvement.',
        );
      }
      // Refresh the document (sections + fix data) and sync the checklist tab.
      await refreshProjectSections();
      notifyPeerReviewChanged();
    } catch {
      /* Non-fatal; a manual refresh will reconcile. */
    } finally {
      setApplyingFullDocumentAction(null);
    }
  };

  /**
   * Download the full architecture document in the selected format.
   */
  const handleExport = async (format: string) => {
    if (!projectId || exportingFormat !== null) {
      return;
    }

    setExportingFormat(format);
    setExportError(null);

    try {
      const blob = await generationService.exportDocument(
        Number.parseInt(projectId, 10),
        format,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const safeTitle = project?.title
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase() ?? DEFAULT_DOCUMENT_FILENAME;
      link.download = `${safeTitle}${
        EXPORT_EXTENSION_BY_FORMAT[format] ?? DEFAULT_EXPORT_EXTENSION
      }`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : EXPORT_FAILED_MESSAGE;
      setExportError(message);
    } finally {
      setExportingFormat(null);
    }
  };

  /**
   * Handle adding a new reviewer
   */
  const handleAddNewReviewer = async () => {
    if (!reviewId || !newReviewerEmail.trim() || !canManageParticipants) {
      return;
    }

    if (!newReviewerRole) {
      alert(PARTICIPANT_ROLE_REQUIRED_MESSAGE);
      return;
    }

    if (
      project &&
      !canSelfReview &&
      isProjectStewardUser(
        { email: newReviewerEmail.trim(), username: newReviewerEmail.trim() },
        project,
      )
    ) {
      alert(PROJECT_STEWARD_PARTICIPANT_MESSAGE);
      return;
    }

    setAddingReviewer(true);
    try {
      await reviewService.addReviewers(reviewId, {
        reviewers: [
          { email: newReviewerEmail.trim(), role: newReviewerRole },
        ],
      });

      setNewReviewerEmail('');
      setNewReviewerRole('');
      // Keep modal open to allow managing reviewers
      await loadReviewData(); // Reload to show new reviewer
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : REVIEWER_ADD_FAILED_MESSAGE;
      alert(message);
    } finally {
      setAddingReviewer(false);
    }
  };

  /**
   * Handle removing a reviewer
   */
  const handleRemoveReviewer = async (reviewerId: string) => {
    if (!reviewId || !canManageParticipants) {
      alert(PARTICIPANT_MANAGEMENT_FORBIDDEN_MESSAGE);
      return;
    }

    if (!globalThis.confirm('Are you sure you want to remove this reviewer?')) {
      return;
    }

    setRemovingReviewer(reviewerId);
    try {
      await reviewService.removeReviewer(reviewId, reviewerId);
      await loadReviewData(); // Reload to update list
    } catch (err) {
      logger.error('Failed to remove reviewer:', err);
      alert('Failed to remove reviewer. Please try again.');
    } finally {
      setRemovingReviewer(null);
    }
  };

  /**
   * Update chat context when interacting with a section
   */
  const handleSectionInteract = (sectionId: string, sectionTitle: string) => {
    contextManager.updateContext('current_section_id', sectionId, 'review');
    contextManager.updateContext('current_section_title', sectionTitle, 'review');
    contextManager.updateContext('active_context', 'review_section', 'navigation');
  };

  /**
   * Load review and project data
   */
  useEffect(() => {
    if (reviewId && projectId) {
      // Set initial review context
      contextManager.updateContext('project_id', projectId, 'navigation');
      contextManager.updateContext('review_id', reviewId, 'review');
      contextManager.updateContext('stage_name', 'review', 'navigation');
      
      void loadReviewData();
    }
    
    return () => {
      // Cleanup review context on unmount
      contextManager.removeContext('review_id');
      contextManager.removeContext('current_section_id');
      contextManager.removeContext('current_section_title');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId, projectId, user?.email, user?.username]);

  const loadReviewData = async () => {
    if (!reviewId || !projectId) {
      logger.error('[ReviewDetailPage] Missing IDs:', { reviewId, projectId });
      setError('Missing review or project ID');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Load review details
      const [reviewData, projectData] = await Promise.all([
        reviewService.getReview(reviewId),
        projectService.getProject(Number.parseInt(projectId, 10)),
      ]);
      setReview(reviewData);
      setProject(projectData);

      const currentUserIdentity = user?.email ?? user?.username ?? '';
      if (!currentUserIdentity) {
        setError('Unable to determine authenticated user email.');
        setLoading(false);
        return;
      }

      const visibleParticipants = reviewData.reviewers.filter(
        (reviewer) => !isProjectStewardReviewer(reviewer, projectData),
      );

      // Users with the self-review privilege (admins, for testing) can be
      // both the project owner and an assigned reviewer/approver. Look them
      // up in the full reviewer list so their own participant record is not
      // filtered out by the owner-exclusion rule.
      const reviewerPool = canSelfReview
        ? reviewData.reviewers
        : visibleParticipants;

      // Match the backend participant resolution, which prefers the
      // non-owner row (`order_by(is_owner.asc())`). An admin with the
      // self-review privilege can have both an owner/steward row and an
      // assigned reviewer/approver row; comments are always authored under
      // the non-owner row, so we must select the same row here for the
      // resolve permission check to line up.
      const matchingReviewers = reviewerPool
        .filter((reviewer) => reviewerMatchesCurrentUser(reviewer, user))
        .sort(
          (a, b) =>
            Number(a.is_owner ?? false) - Number(b.is_owner ?? false),
        );
      const currentReviewer = matchingReviewers[0];
      // The project owner/co-owner (steward) may edit the Archie checklist even
      // when they are also a participant in the review.
      const steward = isCurrentUserProjectSteward(user, projectData);
      setIsProjectSteward(steward);

      if (currentReviewer) {
        setIsReviewer(true);
        setIsOwner(false);
        setCanApprove(
          currentReviewer.role === 'approver' &&
            (canSelfReview || !(currentReviewer.is_owner ?? false)),
        );
        setCurrentReviewerId(currentReviewer.id);
      } else if (steward) {
        setIsReviewer(false);
        setIsOwner(true);
        setCanApprove(false);
        setCurrentReviewerId(null);
      } else {
        logger.error('User is NOT a participant for this review');
        setError(REVIEW_ACCESS_DENIED_MESSAGE);
        setIsReviewer(false);
        setIsOwner(false);
        setCanApprove(false);
        setLoading(false);
        return;
      }

      // Load project sections
      const projectSections = await projectService.getSections(
        Number.parseInt(projectId, 10),
        { selectedOnly: true },
      );
      setSections(projectSections);
      setComments(reviewData.comments || []);
    } catch (err) {
      logger.error('Error loading review data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load review');
    } finally {
      setLoading(false);
    }
  };

  const refreshProjectSections = async () => {
    if (!projectId) {
      return;
    }

    const projectSections = await projectService.getSections(
      Number.parseInt(projectId, 10),
      { selectedOnly: true },
    );
    setSections(projectSections);

    // Also refresh the peer-review items so the new fix's highlight ranges and
    // summary appear in the Full Document tab.
    if (reviewId) {
      try {
        const peerReview = await reviewService.getPeerReview(reviewId);
        setPeerReviewItems(peerReview.items);
      } catch {
        /* Non-fatal. */
      }
    }
  };

  const visibleParticipants = review?.reviewers.filter(
    (reviewer) =>
      !isProjectStewardReviewer(reviewer, project) ||
      // Include the current user's own self-assigned record (admins with the
      // self-review privilege) so the header summary counts it correctly.
      (canSelfReview && reviewerMatchesCurrentUser(reviewer, user)),
  ) ?? [];
  const canManageParticipants = Boolean(
    project && isCurrentUserProjectSteward(user, project),
  );
  // Participants are locked once the review is approved (audit integrity).
  const isReviewApproved = review?.status === APPROVED_REVIEW_STATUS;
  const canReplyToComments = isReviewer || isOwner;
  const participantCounts = getReviewParticipantCounts(visibleParticipants);
  const participantSummary = formatParticipantSummary(participantCounts);
  // Approval progress reflects approver-role participants, who are the
  // decision-makers for approve/request-changes.
  const approverParticipants = visibleParticipants.filter(
    (reviewer) => reviewer.role === APPROVER_ROLE,
  );
  const approvedApproversCount = approverParticipants.filter(
    (reviewer) => reviewer.status === APPROVED_REVIEWER_STATUS,
  ).length;
  const approvedApproversLabel =
    approverParticipants.length === 0
      ? '0 Approved'
      : `${approvedApproversCount}/${approverParticipants.length} Approved`;

  useEffect(() => {
    if (activeTab !== 'sections' || !shouldFocusRequestChanges) {
      return;
    }

    requestChangesSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    setShouldFocusRequestChanges(false);
  }, [activeTab, shouldFocusRequestChanges]);

  useEffect(() => {
    if (activeTab !== 'sections' || !pendingFullDocumentSectionId) {
      return undefined;
    }

    const timer = globalThis.setTimeout(() => {
      const sectionEl =
        peerCommentSectionRefs.current[pendingFullDocumentSectionId];
      sectionEl?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      setPendingFullDocumentSectionId(null);
    }, FULL_DOCUMENT_SECTION_SCROLL_DELAY_MS);

    return () => globalThis.clearTimeout(timer);
  }, [activeTab, pendingFullDocumentSectionId, sections]);

  /**
   * Handle reviewer status update (Approve/Request Changes)
   */
  const openConfirmationModal = (status: ReviewerStatus) => {
    const approveMessage =
      openOwnGeneralCommentsCount > 0
        ? `${UNRESOLVED_COMMENTS_WARNING} This will mark your review as approved and notify the project team.`
        : 'Are you sure you want to approve this architecture review? This will mark your review as approved and notify the project team.';
    const modalConfigs = {
      approved: {
        title: 'Approve Review',
        message: approveMessage,
        variant: 'approve' as ModalVariant,
      },
      'changes_requested': {
        title: 'Request Changes',
        message: 'Are you sure you want to request changes? The project team will be notified and the review status will be updated.',
        variant: 'request-changes' as ModalVariant,
      },
      pending: {
        title: 'Reset to Pending',
        message: 'Reset your review status to pending?',
        variant: 'approve' as ModalVariant,
      },
    };

    const config = modalConfigs[status];
    setModalState({
      isOpen: true,
      action: status,
      variant: config.variant,
      title: config.title,
      message: config.message,
    });
  };

  const handleStatusUpdate = async (comment?: string) => {
    if (!review || !currentReviewerId || !modalState.action) {return;}

    setSubmitting(true);
    try {
      const trimmedComment = comment?.trim() ?? '';
      if (
        modalState.action === 'changes_requested' &&
        trimmedComment.length === 0
      ) {
        setError(REQUEST_CHANGES_COMMENT_REQUIRED_MESSAGE);
        return;
      }

      if (modalState.action === 'changes_requested') {
        await createComment(OVERVIEW_SECTION_ID, trimmedComment, {
          commentType: COMMENT_TYPE_REQUEST_CHANGES,
        });
      } else if (trimmedComment.length > 0) {
        await createComment(
          OVERVIEW_SECTION_ID,
          `**Review Decision: APPROVED**\n\n${trimmedComment}`,
        );
      }

      await reviewService.updateReviewerStatus(
        reviewId!,
        currentReviewerId,
        {
          status: modalState.action,
        },
      );

      // Update local state immediately (no page reload)
      setReview((prev) => {
        if (!prev) {return prev;}
        return {
          ...prev,
          reviewers: prev.reviewers.map((r) =>
            r.id === currentReviewerId
              ? { ...r, status: modalState.action! }
              : r,
          ),
        };
      });

      setModalState({
        isOpen: false,
        action: null,
        variant: 'approve',
        title: '',
        message: '',
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update status',
      );
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle adding a comment to a section
   */
  const createComment = async (
    sectionId: string,
    commentText: string,
    options: {
      parentId?: string;
      commentType?: CommentType;
    } = {},
  ): Promise<Comment> => {
    if (!reviewId) {
      throw new Error('Review ID is required.');
    }

    const createdComment = await reviewService.addComment(reviewId, {
      section_id: sectionId,
      comment: commentText,
      parent_id: options.parentId,
      comment_type: options.commentType,
    });
    setComments((previousComments) => [...previousComments, createdComment]);
    return createdComment;
  };

  const handleAddComment = async (sectionId: string, commentText: string) => {
    if (!reviewId) {return;}

    try {
      await createComment(sectionId, commentText);
    } catch (err) {
      logger.error('Error adding comment:', err);
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Failed to add comment. You may not have permission to comment on this review.';
      setError(errorMessage);
      
      // Show user-friendly alert
      alert(`Cannot add comment: ${errorMessage}\n\nPlease ensure you are assigned as a reviewer for this review.`);
    }
  };

  /**
   * Handle editing a comment
   */
  const handleEditComment = async (commentId: string, newText: string) => {
    if (!reviewId || !newText.trim()) {return;}

    try {
      const updatedComment = await reviewService.updateComment(reviewId, commentId, { comment: newText });
      
      // Optimistic update: Update comment in local state
      setComments(prevComments => 
        prevComments.map(c => c.id === commentId ? updatedComment : c),
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update comment',
      );
    }
  };

  /**
   * Handle deleting a comment
   */
  /**
   * Handle deleting a comment
   */
  const handleDeleteComment = async (commentId: string) => {
    if (!reviewId) {return;}

    try {
      await reviewService.deleteComment(reviewId, commentId);
      
      // Optimistic update: Remove comment from local state
      setComments(prevComments => prevComments.filter(c => c.id !== commentId));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete comment',
      );
    }
  };

  /**
   * Handle replying to a comment
   */
  const handleReplyComment = async (parentId: string, replyText: string) => {
    if (!reviewId || !replyText.trim()) {return;}

    try {
      // Find the parent comment to get its section_id
      const parentComment = comments.find(c => c.id === parentId);
      if (!parentComment) {
        logger.error('Parent comment not found');
        return;
      }

      await createComment(parentComment.section_id, replyText, {
        parentId,
      });
    } catch (err) {
      logger.error('Error adding reply:', err);
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Failed to add reply.';
      setError(errorMessage);
      alert(`Cannot add reply: ${errorMessage}`);
    }
  };

  /**
   * Handle resolving/unresolving a comment
   */
  const handleToggleResolve = async (
    commentId: string,
    currentStatus: string,
  ) => {
    if (!reviewId) {return;}

    try {
      const newStatus = currentStatus === 'open' ? 'resolved' : 'open';
      await reviewService.updateCommentStatus(reviewId, commentId, {
        status: newStatus,
      });
      
      // Update local state (no page reload)
      await loadReviewData();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update comment',
      );
    }
  };

  /**
   * Group comments by section
   */
  const commentsBySection = getCommentsBySection(comments);
  const overviewComments = commentsBySection[OVERVIEW_SECTION_ID] ?? [];
  const requestChangeThreadIds = getRequestChangeThreadIds(overviewComments);
  const requestChangeComments = overviewComments.filter((comment) =>
    requestChangeThreadIds.has(comment.id),
  );
  // Final approval decisions (and their replies) are surfaced in the top
  // "Review Decisions" section alongside requested changes, separated from
  // regular comments.
  const approvalDecisionThreadIds = new Set<string>(
    overviewComments
      .filter(
        (comment) =>
          !comment.parent_id &&
          !requestChangeThreadIds.has(comment.id) &&
          comment.comment.startsWith(APPROVAL_DECISION_PREFIX),
      )
      .map((comment) => comment.id),
  );
  overviewComments.forEach((comment) => {
    if (comment.parent_id && approvalDecisionThreadIds.has(comment.parent_id)) {
      approvalDecisionThreadIds.add(comment.id);
    }
  });
  const reviewDecisionComments = overviewComments.filter(
    (comment) =>
      requestChangeThreadIds.has(comment.id) ||
      approvalDecisionThreadIds.has(comment.id),
  );
  const generalOverviewComments = overviewComments.filter(
    (comment) =>
      !requestChangeThreadIds.has(comment.id) &&
      !approvalDecisionThreadIds.has(comment.id),
  );
  const openRequestChangesCount = comments.filter(
    (comment) =>
      comment.comment_type === COMMENT_TYPE_REQUEST_CHANGES &&
      comment.status === 'open' &&
      !comment.parent_id,
  ).length;
  // Open, top-level regular comments authored by the current reviewer. Used to
  // warn an approver who still has unresolved comments before approving.
  const openOwnGeneralCommentsCount = comments.filter(
    (comment) =>
      comment.comment_type !== COMMENT_TYPE_REQUEST_CHANGES &&
      comment.status === 'open' &&
      !comment.parent_id &&
      currentReviewerId !== null &&
      comment.reviewer_id === currentReviewerId,
  ).length;
  // Only approvers own review decisions and checklist checks.
  const canRequestChanges = canApprove && currentReviewerId !== null;
  const conversationCount = comments.filter((c) => !c.parent_id).length;

  const openRequestedChangesView = () => {
    setActiveTab('sections');
    setShouldFocusRequestChanges(true);
  };

  // "View change" lands in the Peer Review Comments tab (where the change is
  // shown as an Archie comment + highlighted in the section preview) and
  // scrolls to that section.
  const openFullDocumentSection = (sectionName: string) => {
    setPendingFullDocumentSectionId(sectionName);
    setActiveTab('sections');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
        <p className="ml-4 text-base-content/60">Loading review data...</p>
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <ErrorMessage message={error || 'Review not found'} />
        <button
          onClick={() => navigate('/?tab=reviews')}
          className="mt-4 px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 main-button"
        >
          Back to Reviews
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-base-200">
      {/* Header */}
      <ReviewHeader
        onBack={() => navigate('/?tab=reviews')}
        canManageParticipants={canManageParticipants}
        isReviewApproved={isReviewApproved}
        onEditParticipants={() => setShowManageReviewersModal(true)}
        visibleParticipants={visibleParticipants}
        participantSummary={participantSummary}
        approvedApproversLabel={approvedApproversLabel}
        review={review}
        conversationCount={conversationCount}
        canRequestChanges={canRequestChanges}
        canApprove={canApprove}
        currentReviewerId={currentReviewerId}
        openRequestChangesCount={openRequestChangesCount}
        allPeerReviewChecked={allPeerReviewChecked}
        submitting={submitting}
        onOpenConfirmationModal={openConfirmationModal}
        onOpenRequestedChangesView={openRequestedChangesView}
        isReviewer={isReviewer}
        isOwner={isOwner}
        canSelfReview={canSelfReview}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Tabs */}
          <div className="mb-6 border-b border-gray-200">
            <nav className="flex gap-4">
              <button
                onClick={() => setActiveTab(PEER_CHECKLIST_TAB_ID)}
                className={getReviewTabClassName(activeTab === PEER_CHECKLIST_TAB_ID)}
              >
                <ClipboardDocumentCheckIcon className="w-5 h-5" />
                Peer Review Checklist
              </button>
              <button
                onClick={() => setActiveTab('sections')}
                className={getReviewTabClassName(activeTab === 'sections')}
              >
                <ChatBubbleLeftRightIcon className="w-5 h-5" />
                Peer Review Comments
              </button>
              <button
                onClick={() => setActiveTab('full')}
                className={getReviewTabClassName(activeTab === 'full')}
              >
                <DocumentTextIcon className="w-5 h-5" />
                Full Document
              </button>
            </nav>
          </div>

          {/* Tab Content: Review by Section */}
          {activeTab === 'sections' && (
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
              {/* Table-of-contents sidebar: jump to any section or sub-heading. */}
              {renderDocumentTocSidebar(peerCommentSectionRefs)}

              {/* Comment threads */}
              <div className="min-w-0 flex-1 space-y-6">
                {reviewDecisionComments.length > 0 && (
                  <div ref={requestChangesSectionRef}>
                    <SectionCommentThread
                      sectionId="review-decisions"
                      sectionTitle={
                        requestChangeComments.length > 0
                          ? REQUEST_CHANGES_SECTION_TITLE
                          : REVIEW_DECISIONS_SECTION_TITLE
                      }
                      sectionContent=""
                      comments={reviewDecisionComments}
                      isReviewer={isReviewer}
                      isOwner={isOwner} // Added isOwner prop
                      canReply={canReplyToComments}
                      canAddComment={false}
                      currentReviewerId={currentReviewerId}
                      onAddComment={async () => undefined}
                      onEditComment={handleEditComment}
                      onDeleteComment={handleDeleteComment}
                      onReplyComment={handleReplyComment}
                      onToggleResolve={handleToggleResolve}
                      currentUserEmail={currentUserEmail}
                      onInteract={handleSectionInteract}
                      hideResolveButton={isReviewApproved}
                    />
                  </div>
                )}

                {/* General Comments (No Section Content) */}
                <div ref={generalCommentsRef}>
                  <SectionCommentThread
                    sectionId={OVERVIEW_SECTION_ID}
                    sectionTitle="💬 General Review Comments"
                    sectionContent=""
                    comments={generalOverviewComments}
                    isReviewer={isReviewer}
                    isOwner={isOwner} // Added isOwner prop
                    canReply={canReplyToComments}
                    canAddComment={isReviewer}
                    currentReviewerId={currentReviewerId}
                    onAddComment={(text) => handleAddComment('overview', text)}
                    onEditComment={handleEditComment}
                    onDeleteComment={handleDeleteComment}
                    onReplyComment={handleReplyComment}
                    onToggleResolve={handleToggleResolve}
                    currentUserEmail={currentUserEmail}
                    onInteract={handleSectionInteract}
                    hideResolveButton={isReviewApproved}
                  />
                </div>

                {/* Section-by-section Review */}
                <div className="space-y-4">
                  {sections.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-base-content/60">
                    No sections found for this project.
                      </p>
                    </div>
                  ) : (
                    sections.map((section) => {
                      const fixSummary = sectionFixSummaries.get(section.id);
                      return (
                        <div
                          key={section.id}
                          className="scroll-mt-6"
                          ref={(element) => {
                            peerCommentSectionRefs.current[section.id] = element;
                          }}
                        >
                          <SectionCommentThread
                            sectionId={section.id}
                            sectionTitle={section.title}
                            sectionContent={section.content || ''}
                            comments={commentsBySection[section.id] || []}
                            isReviewer={isReviewer}
                            isOwner={isOwner} // Added isOwner prop
                            canReply={canReplyToComments}
                            canAddComment={isReviewer}
                            currentReviewerId={currentReviewerId}
                            onAddComment={(text) =>
                              handleAddComment(section.id, text)
                            }
                            onEditComment={handleEditComment}
                            onDeleteComment={handleDeleteComment}
                            onReplyComment={handleReplyComment}
                            onToggleResolve={handleToggleResolve}
                            currentUserEmail={currentUserEmail}
                            onInteract={handleSectionInteract}
                            hideResolveButton={isReviewApproved}
                            headingIdPrefix={`sec-${section.id}`}
                            archieChangeDescriptions={archieDescriptionsBySection.get(
                              section.id,
                            )}
                            // Archie's applied fix shown as the first comment in the
                            // thread (same card/styling, just placed inside the
                            // comment list), mirroring the Full Document Keep/Undo.
                            pinnedContent={
                              fixSummary ? (
                                <ArchieSectionFixCard
                                  summary={fixSummary}
                                  sectionId={section.id}
                                  canDecide={isProjectSteward}
                                  isApplying={Boolean(
                                    applyingFullDocumentAction?.startsWith(
                                      `${section.id}:`,
                                    ),
                                  )}
                                  onAction={handleFullDocumentAction}
                                />
                              ) : undefined
                            }
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab Content: Peer Review Checklist.
              The human approver's read-only sign-off view: results + comments +
              the approver check column. Only the assigned approver can tick
              items (matches the backend approver-only gate); Archie's tooling
              (recommendation, debate, fix/re-run/submit) is stripped out. */}
          {activeTab === PEER_CHECKLIST_TAB_ID && reviewId && (
            <PeerReviewTable
              variant="peer-approval"
              reviewId={reviewId}
              projectId={projectId ? Number.parseInt(projectId, 10) : 0}
              canApprove={canApprove}
              canUseChat={false}
              // Any participant (reviewer/approver) or owner/co-owner may leave
              // architect-review comments here; they're shared with the Archie
              // Review Checklist tab (same architect_comments). Fix decisions
              // (Keep/Undo) stay off in this view - handled by the variant.
              canEditArchitectReview={isReviewer || isProjectSteward}
              canFix={false}
              onOpenFixSection={openFullDocumentSection}
              onChecklistStatusChange={setAllPeerReviewChecked}
            />
          )}

          {/* Tab Content: Full Document View */}
          {activeTab === 'full' && (
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
              {/* Table-of-contents sidebar: jump to any section or ADR. */}
              {renderDocumentTocSidebar(fullDocumentSectionRefs)}

              <div className="min-w-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-col gap-4 p-4 bg-gray-50 border-b border-gray-200 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      📄 Complete Architecture Document
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Read-only view of the full document
                    </p>
                    {exportError && (
                      <p className="mt-2 text-sm text-red-600">{exportError}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap justify-start gap-2 lg:justify-end">
                    {EXPORT_OPTIONS.map(({ format, label }) => {
                      const isExporting = exportingFormat === format;
                      const isDisabled = exportingFormat !== null;

                      return (
                        <button
                          key={format}
                          onClick={() => {
                            void handleExport(format);
                          }}
                          disabled={isDisabled}
                          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-[#336094] hover:text-[#336094] disabled:cursor-not-allowed disabled:opacity-50"
                          title={`Download full document as ${label}`}
                        >
                          {isExporting ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : (
                            <DocumentArrowDownIcon className="h-4 w-4" />
                          )}
                          {isExporting ? `Exporting ${label}…` : label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-6 overflow-hidden bg-white p-4">
                  {sections.map((section) => (
                    <div
                      key={section.id}
                      ref={(element) => {
                        fullDocumentSectionRefs.current[section.id] = element;
                      }}
                      className="scroll-mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white"
                    >
                      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                        <h3 className="text-base font-semibold text-gray-900">
                          {section.title}
                        </h3>
                      </div>
                      <MonacoMarkdownEditor
                        value={section.content}
                        onChange={() => {/* read-only */}}
                        fitContent={true}
                        theme="light"
                        previewOnly={true}
                        headingIdPrefix={`sec-${section.id}`}
                        archieChangeDescriptions={archieDescriptionsBySection.get(
                          section.id,
                        )}
                      />
                    </div>
                  ))}
                </div>
                <style>{`
                  .${FIX_HIGHLIGHT_CLASSNAME} {
                    background-color: ${FIX_ADDED_BACKGROUND};
                    border-bottom: 2px solid ${FIX_ADDED_BORDER};
                    border-radius: 2px;
                    padding: 0 1px;
                    box-shadow: inset 3px 0 0 ${FIX_ADDED_BORDER};
                    position: relative;
                  }

                  .${FIX_HIGHLIGHT_CLASSNAME}::after {
                    content: '+ Added';
                    display: inline-block;
                    margin-left: 6px;
                    padding: 0 4px;
                    border-radius: 999px;
                    background: ${FIX_ADDED_BORDER};
                    color: #ffffff;
                    font-size: 10px;
                    font-weight: 600;
                  }

                  .${FIX_HIGHLIGHT_REMOVED_CLASSNAME} {
                    background-color: ${FIX_REMOVED_BACKGROUND};
                    border-bottom: 2px solid ${FIX_REMOVED_BORDER};
                    border-radius: 2px;
                    padding: 0 1px;
                    box-shadow: inset 3px 0 0 ${FIX_REMOVED_BORDER};
                    position: relative;
                  }

                  .${FIX_HIGHLIGHT_REMOVED_CLASSNAME}::after {
                    content: '- Removed';
                    display: inline-block;
                    margin-left: 6px;
                    padding: 0 4px;
                    border-radius: 999px;
                    background: ${FIX_REMOVED_BORDER};
                    color: #ffffff;
                    font-size: 10px;
                    font-weight: 600;
                  }
                `}</style>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={() =>
          setModalState({
            isOpen: false,
            action: null,
            variant: 'approve',
            title: '',
            message: '',
          })
        }
        onConfirm={handleStatusUpdate}
        title={modalState.title}
        message={modalState.message}
        confirmText={modalState.action === 'approved' ? 'Approve Review' : 'Request Changes'}
        variant={modalState.variant}
        isLoading={submitting}
        showCommentInput={true}
        commentPlaceholder={
          modalState.action === 'changes_requested'
            ? 'Describe the requested changes...'
            : 'Add an optional comment (will be added to general comments)...'
        }
        isCommentRequired={modalState.action === 'changes_requested'}
        commentValidationMessage={REQUEST_CHANGES_COMMENT_REQUIRED_MESSAGE}
      />

      {/* Manage Reviewers Modal */}
      <ManageReviewersModal
        isOpen={showManageReviewersModal}
        canManageParticipants={canManageParticipants}
        isReviewApproved={isReviewApproved}
        onClose={() => setShowManageReviewersModal(false)}
        visibleParticipants={visibleParticipants}
        removingReviewer={removingReviewer}
        onRemoveReviewer={handleRemoveReviewer}
        newReviewerEmail={newReviewerEmail}
        onNewReviewerEmailChange={setNewReviewerEmail}
        newReviewerRole={newReviewerRole}
        onNewReviewerRoleChange={setNewReviewerRole}
        onAddNewReviewer={handleAddNewReviewer}
        addingReviewer={addingReviewer}
      />
    </div>
  );
};
